import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  create_entity_type_workflow,
  fetch_entity_type_workflow_forms,
  fetch_entity_type_workflows,
  update_entity_type_workflow,
} from '@/services/workflow_service';

export default {
  name: 'CreateWorkflow',
  mixins: [alert_mixin],
  created() {
    this.load_workflows();
  },
  data() {
    return {
      hero: {
        title: 'Workflow Management Hub',
        description:
          'Orchestrate cross-departmental logic, automated approvals, and data synchronization triggers from a single interface.',
      },
      workflows: [],
      workflows_loading: false,
      workflow_status_filter: 'all',
      workflow_sort_order: 'updated_desc',
      workflow_current_page: 1,
      workflow_page_size: 10,
      create_workflow_form: {
        name: '',
        description: '',
      },
      create_workflow_running: false,
      edit_workflow_form: {
        workflow_id: null,
        name: '',
        description: '',
      },
      edit_workflow_running: false,
      modal_error_message: '',
      edit_modal_error_message: '',
    };
  },
  computed: {
    route_entity_type_id() {
      const entity_type_id = Number(this.$route?.query?.entityTypeId || 0);
      return Number.isFinite(entity_type_id) && entity_type_id > 0 ? entity_type_id : null;
    },
    route_project_id() {
      const project_id = Number(this.$route?.query?.project_id || this.$route?.query?.projectId || 0);
      return Number.isFinite(project_id) && project_id > 0 ? project_id : null;
    },
    filtered_workflows() {
      const filter_value = String(this.workflow_status_filter || 'all').toLowerCase();
      const sort_value = String(this.workflow_sort_order || 'updated_desc').toLowerCase();

      const filtered = (Array.isArray(this.workflows) ? [...this.workflows] : []).filter((workflow) => {
        const normalized_status = String(workflow?.status || '').toLowerCase();

        if (filter_value === 'active') {
          return normalized_status === 'active';
        }

        if (filter_value === 'inactive') {
          return normalized_status !== 'active';
        }

        return true;
      });

      return filtered.sort((first, second) => {
        const first_updated = new Date(first?.updatedAt || 0).getTime() || 0;
        const second_updated = new Date(second?.updatedAt || 0).getTime() || 0;
        const first_name = String(first?.title || '').toLowerCase();
        const second_name = String(second?.title || '').toLowerCase();

        if (sort_value === 'updated_asc') {
          return first_updated - second_updated;
        }

        if (sort_value === 'name_asc') {
          return first_name.localeCompare(second_name);
        }

        if (sort_value === 'name_desc') {
          return second_name.localeCompare(first_name);
        }

        return second_updated - first_updated;
      });
    },
    workflow_total_pages() {
      const total_items = this.filtered_workflows.length;
      const page_size = Number(this.workflow_page_size) || 10;
      return Math.max(1, Math.ceil(total_items / page_size));
    },
    paginated_workflows() {
      const page_size = Number(this.workflow_page_size) || 10;
      const current_page = Math.min(this.workflow_current_page, this.workflow_total_pages);
      const start_index = (current_page - 1) * page_size;
      return this.filtered_workflows.slice(start_index, start_index + page_size);
    },
    workflow_visible_page_numbers() {
      return Array.from(
        { length: this.workflow_total_pages },
        (_, index) => index + 1
      );
    },
    pagination_start_item() {
      if (!this.filtered_workflows.length) {
        return 0;
      }

      return ((this.workflow_current_page - 1) * this.workflow_page_size) + 1;
    },
    pagination_end_item() {
      return Math.min(
        this.workflow_current_page * this.workflow_page_size,
        this.filtered_workflows.length
      );
    }
  },
  watch: {
    '$route.query.entityTypeId'() {
      this.load_workflows();
    },
    workflow_status_filter() {
      this.workflow_current_page = 1;
    },
    workflow_sort_order() {
      this.workflow_current_page = 1;
    },
    workflow_page_size() {
      this.workflow_current_page = 1;
    },
    workflows() {
      if (this.workflow_current_page > this.workflow_total_pages) {
        this.workflow_current_page = this.workflow_total_pages;
      }
    },
  },
  methods: {
    build_workflow_form_payload(workflow_id) {
      const normalized_workflow_id = Number(workflow_id || 0);
      const payload = {
        entity_workflow_id: normalized_workflow_id,
        entity_type_workflow_id: normalized_workflow_id,
        workflow_id: normalized_workflow_id,
      };

      if (this.route_project_id) {
        payload.project_id = Number(this.route_project_id);
      }

      return payload;
    },

    first_non_empty_value(values = []) {
      return values.find((value) => Boolean(value)) || '';
    },

    extract_assigned_form_name(form) {
      if (typeof form === 'string') {
        return form;
      }

      return this.first_non_empty_value([
        form?.form_name,
        form?.name,
        form?.title,
        form?.label,
        form?.form?.form_name,
        form?.form?.name,
        form?.form?.title,
        form?.form_title,
      ]);
    },

    normalize_assigned_forms(assigned_forms = []) {
      const assigned_form_ids = assigned_forms
        .filter((form) => this.form_assignment_enabled(form))
        .map((form) => this.extract_assigned_form_id(form))
        .filter((id) => id);
      const assigned_form_binding_ids = assigned_forms
        .filter((form) => this.form_assignment_enabled(form))
        .reduce((accumulator, form) => {
          const form_id = this.extract_assigned_form_id(form);
          const binding_id = Number(form?.id || form?.entity_workflow_form_id || 0);

          if (form_id && binding_id) {
            accumulator[String(form_id)] = binding_id;
          }

          return accumulator;
        }, {});
      const assigned_form_names = assigned_forms
        .filter((form) => this.form_assignment_enabled(form))
        .map((form) => this.extract_assigned_form_name(form))
        .filter(Boolean);

      return {
        assigned_form_ids: [...new Set(assigned_form_ids)],
        assigned_form_binding_ids,
        assigned_form_names: [...new Set(assigned_form_names)],
      };
    },

    format_workflow_date(value) {
      if (!value) {
        return '';
      }

      const parsed_date = new Date(value);

      if (Number.isNaN(parsed_date.getTime())) {
        return '';
      }

      return new Intl.DateTimeFormat('en-ZA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(parsed_date).replace(',', '');
    },

    workflow_creator_label(workflow = {}) {
      const creator = workflow?.creator || {};
      const first_name = String(creator?.first_name || '').trim();
      const last_name = String(creator?.last_name || '').trim();
      const creator_name = [
        workflow?.creator_name,
        workflow?.created_by_name,
        workflow?.creator_full_name,
        workflow?.created_by_full_name,
        creator?.full_name,
        `${first_name} ${last_name}`.trim(),
        creator?.username,
        creator?.email,
      ].find((value) => String(value || '').trim());

      return String(creator_name || '').trim();
    },

    workflow_form_short_label(form_name = '') {
      const words = String(form_name)
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (!words.length) {
        return 'F';
      }

      if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
      }

      return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
    },

    workflow_assigned_forms_display(forms = []) {
      const normalized_forms = Array.isArray(forms) ? forms.filter(Boolean) : [];
      const shown = normalized_forms.slice(0, 2).map((form_name, index) => ({
        key: `${form_name}-${index}`,
        label: this.workflow_form_short_label(form_name),
        name: form_name,
        is_more: false,
      }));

      if (normalized_forms.length > shown.length) {
        shown.push({
          key: 'more',
          label: `+${normalized_forms.length - shown.length}`,
          is_more: true,
        });
      }

      return shown;
    },

    go_to_workflow_page(page) {
      const normalized_page = Number(page) || 1;
      this.workflow_current_page = Math.min(
        Math.max(normalized_page, 1),
        this.workflow_total_pages
      );
    },

    get_status_class(normalized_status) {
      if (normalized_status === 'active') {
        return 'workflow-status-active';
      }

      if (normalized_status === 'archived') {
        return 'workflow-status-archived';
      }

      return 'workflow-status-pending';
    },

    get_indicator_class(normalized_status) {
      if (normalized_status === 'active') {
        return 'workflow-indicator-success';
      }

      if (normalized_status === 'archived') {
        return 'workflow-indicator-muted';
      }

      return 'workflow-indicator-warning';
    },

    open_create_workflow_modal() {
      this.modal_error_message = '';
      this.create_workflow_form = {
        name: '',
        description: '',
      };
    },

    open_edit_workflow_modal(workflow = {}) {
      this.edit_modal_error_message = '';
      this.edit_workflow_form = {
        workflow_id: workflow?.id || null,
        name: workflow?.title || workflow?.name || '',
        description: workflow?.description || '',
      };
    },

    go_to_configure_workflow(workflow = {}) {
      this.$router.push({
        name: 'ConfigureWorkflow',
        query: {
          workflowId: workflow?.id ? String(workflow.id) : '',
          workflowName: workflow?.title || workflow?.name || 'Workflow',
          entityTypeId: this.route_entity_type_id ? String(this.route_entity_type_id) : '',
          projectId: this.route_project_id ? String(this.route_project_id) : '',
          tab: 'forms',
        },
      });
    },

    map_workflow_to_card(workflow = {}) {
      const title = workflow?.name || workflow?.title || 'Untitled Workflow';
      const description = workflow?.description || 'No description provided.';
      const created_by = this.workflow_creator_label(workflow);
      const created_at = this.format_workflow_date(workflow?.date_created || workflow?.created_at);
      const meta_parts = [
        created_by ? `Created by ${created_by}` : '',
        created_at,
      ].filter(Boolean);
      const created_label = meta_parts.join(' | ') || 'Created just now';
      const status = String(workflow?.workflow_status || workflow?.status || 'NEW').toUpperCase();
      const normalized_status = status.toLowerCase();
      const assigned_forms = Array.isArray(workflow?.assigned_forms) ? workflow.assigned_forms : [];
      const assigned_form_ids = Array.isArray(workflow?.assigned_form_ids) ? workflow.assigned_form_ids : [];
      const updated_at = workflow?.date_updated || workflow?.updated_at || workflow?.date_created || workflow?.created_at || null;

      return {
        id: workflow?.id || `${title}-${Date.now()}`,
        title,
        description,
        status,
        statusClass: this.get_status_class(normalized_status),
        statusMeta: created_label,
        updatedAt: updated_at,
        icon: 'bi bi-wallet2',
        indicatorClass: this.get_indicator_class(normalized_status),
        assignedForms: assigned_forms,
        assignedFormIds: assigned_form_ids,
        emptyState: !assigned_forms.length,
        assignedLabel: 'ASSIGNED FORMS',
      };
    },

    async load_workflows() {
      this.workflows_loading = true;

      try {
        const workflows = await fetch_entity_type_workflows(
          this.route_entity_type_id ? { entity_type_id: this.route_entity_type_id } : {}
        );
        const hydrated_workflows = Array.isArray(workflows)
          ? await Promise.all(
            [...workflows]
              .sort((first, second) => new Date(second?.date_created || 0) - new Date(first?.date_created || 0))
              .map(async (workflow) => {
                try {
                  const forms = await fetch_entity_type_workflow_forms(
                    this.build_workflow_form_payload(workflow?.id)
                  );
                  const assigned_forms = (Array.isArray(forms) ? forms : [])
                    .filter((form) => Boolean(form?.is_assigned))
                    .map((form) => String(form?.form_name || form?.name || '').trim())
                    .filter(Boolean);

                  return {
                    ...workflow,
                    assigned_forms,
                    assigned_form_ids: assigned_forms.map((_, index) => index + 1),
                  };
                } catch {
                  return workflow;
                }
              })
          )
          : [];

        this.workflows = hydrated_workflows.map((workflow) => this.map_workflow_to_card(workflow));
      } catch (error) {
        this.workflows = [];
        this.show_error(error?.response?.data?.message || 'Failed to load workflows.');
      } finally {
        this.workflows_loading = false;
        if (this.workflow_current_page > this.workflow_total_pages) {
          this.workflow_current_page = this.workflow_total_pages;
        }
      }
    },

    async create_workflow() {
      this.modal_error_message = '';

      if (!this.create_workflow_form.name.trim()) {
        this.modal_error_message = 'Please enter a workflow name.';
        return;
      }

      this.create_workflow_running = true;
      const loader = await this.show_loader('Creating workflow...');

      try {
        const payload = {
          name: this.create_workflow_form.name.trim(),
          description: this.create_workflow_form.description.trim(),
        };

        if (this.route_entity_type_id) {
          payload.entity_type_id = this.route_entity_type_id;
        }

        if (this.route_project_id) {
          payload.project_id = this.route_project_id;
        }

        const response = await create_entity_type_workflow(payload);
        const workflow_data = response?.data && !Array.isArray(response.data)
          ? response.data
          : response;
        const workflow_created = response?.status === 'success'
          || response?.success === true
          || Boolean(workflow_data?.id || workflow_data?.name || workflow_data?.title);

        if (workflow_created) {
          await this.load_workflows();
          this.show_success(response?.message || 'Workflow created successfully.');
          this.close_modal('create_workflow');
          this.open_create_workflow_modal();
          return;
        }

        this.show_error(response?.message || 'Failed to create workflow.');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to create workflow.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.create_workflow_running = false;
      }
    },

    async update_workflow() {
      this.edit_modal_error_message = '';

      if (!this.edit_workflow_form.workflow_id) {
        this.edit_modal_error_message = 'Workflow id is missing.';
        return;
      }

      if (!this.edit_workflow_form.name.trim()) {
        this.edit_modal_error_message = 'Please enter a workflow name.';
        return;
      }

      this.edit_workflow_running = true;
      const loader = await this.show_loader('Updating workflow...');

      try {
        const payload = {
          workflow_id: this.edit_workflow_form.workflow_id,
          name: this.edit_workflow_form.name.trim(),
          description: this.edit_workflow_form.description.trim(),
        };

        const response = await update_entity_type_workflow(payload);
        const updated = response?.status === 'success' || response?.success === true;

        if (updated) {
          await this.load_workflows();
          this.show_success(response?.message || 'Workflow updated successfully.');
          this.close_modal('edit_workflow');
          return;
        }

        this.show_error(response?.message || 'Failed to update workflow.');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to update workflow.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.edit_workflow_running = false;
      }
    },
  },
};
